import { makeAutoObservable } from "mobx"

import MatrixStore from "@components/ui/Spreadsheet/state/MatrixStore"
import { Filename } from "@framework/types/company"
import { QueryFilterData } from "@framework/types/query-filter"
import {
  createMatrix,
  createMatrixFromSnapshot,
} from "@components/ui/Spreadsheet/createMatrix"
import {
  CellValidationRule,
  MatrixSnapshot,
} from "@components/ui/Spreadsheet/types"
import { EntityOption } from "@framework/types/common"

import SimpleCollectionStore from "./simple-entity-collection.store"
import MatrixSnapshotStore from "./matrix-snapshot.store"
import UnifiedMatrixController from "./unified-matrix.controller"

export default class UnifiedMatrixStore {
  /**
   * @key solution ID
   */
  instances: Map<string, MatrixStore>

  filesStore: SimpleCollectionStore<Filename>

  productsStore: SimpleCollectionStore<QueryFilterData>

  accountsStore: SimpleCollectionStore<EntityOption>

  snapshotStore: MatrixSnapshotStore

  isLoading: boolean = false

  error: string | null = null

  constructor() {
    this.instances = new Map()
    this.filesStore = new SimpleCollectionStore()
    this.productsStore = new SimpleCollectionStore()
    this.accountsStore = new SimpleCollectionStore()
    this.snapshotStore = new MatrixSnapshotStore()

    makeAutoObservable(this)
  }

  initInstance = async (
    solutionID: string,
    controller: UnifiedMatrixController,
    newInstancePreset?: MatrixSnapshot | null,
    initNew = true
  ) => {
    try {
      this.isLoading = true
      this.error = null

      const validationRules: Record<string, CellValidationRule> = {
        DROPDOWN_FILE: {
          type: "AUTOCOMPLETE",
          search: controller.fetchFiles,
        },
        DROPDOWN_PRODUCT: {
          type: "AUTOCOMPLETE",
          search: controller.fetchProducts,
        },
        DROPDOWN_ACCOUNT: {
          type: "AUTOCOMPLETE",
          search: controller.fetchAccounts,
        },
      }

      const snapshot = initNew
        ? newInstancePreset
        : this.snapshotStore.getSnapshot(solutionID)

      const newInstance =
        snapshot == null
          ? createMatrix({
              validationRules,
              readonly: true,
            })
          : createMatrixFromSnapshot({
              snapshot,
              validationRules,
              readonly: true,
            })

      this.instances.set(solutionID, newInstance)

      return newInstance
    } catch (error) {
      this.error = "Initialization failed"
    }

    return null
  }

  getMatrixInstance = (solutionID: string): MatrixStore | null => {
    return this.instances.get(solutionID) ?? null
  }

  dropInstance = (solutionID: string) => {
    this.instances.delete(solutionID)
  }
}
